import React, { useEffect, useState } from 'react';
import { Modal, Descriptions } from 'antd';
import { getVisitStatusLabel, getVisitStatusBadgeClass } from '../utils/visitStatus';
import { getLocalDateString } from '../utils/date';
import { apiFetch } from '../api';
import { getTranslatedLabel } from '../utils/companyRoles';

const formatLogTime = (value) => (
  value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '---'
);

const VisitDetailsModal = ({ open, onCancel, visit }) => {
  const [formFields, setFormFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(true);

  useEffect(() => {
    if (open && formFields.length === 0) {
      setLoadingFields(true);
      const fetchFields = async () => {
        try {
          const response = await apiFetch('/api/visitor-fields');
          const data = await response.json();
          if (data.success && data.data?.fields) {
             setFormFields(data.data.fields.filter(f => f.enabled).sort((a,b) => (a.order || 0) - (b.order || 0)));
          }
        } catch (e) {
          console.error('Failed to load fields', e);
        } finally {
          setLoadingFields(false);
        }
      };
      fetchFields();
    }
  }, [open, formFields.length]);

  if (!visit) return null;

  const visitor = visit.visitor || {};
  const host = visit.host || {};
  const customFields = visitor.customFields || {};
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const getFieldValue = (key) => {
    if (['firstName', 'lastName', 'email', 'mobileNumber'].includes(key)) {
      return visitor[key];
    }
    if (key === 'hostId' || key === 'personToMeet') {
      const name = `${host.firstName || ''} ${host.lastName || ''}`.trim();
      if (name) return name;
    }
    if (key === 'expectedDate') return visit.expectedDate ? visit.expectedDate : (customFields[key] || visit.createdAt);
    if (key === 'purpose') return visit.purpose;
    
    if (key === 'addr_line1') return visitor.addressLine1;
    if (key === 'addr_city') return visitor.city;
    if (key === 'addr_state') return visitor.state;

    return customFields[key] !== undefined ? customFields[key] : visit[key];
  };

  const getDuration = () => {
    if (visit.checkInTime && visit.checkOutTime) {
      const diffMs = new Date(visit.checkOutTime) - new Date(visit.checkInTime);
      const diffMins = Math.round(diffMs / 60000);
      if (diffMins < 60) return `${diffMins} min`;
      const hrs = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `${hrs}h ${mins}m`;
    }
    return '---';
  };

  return (
    <Modal
      title="Visit Details"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <div className="flex flex-row gap-4 mb-4 mt-2">
        {visitor.photo ? (
          <img src={visitor.photo} alt="Visitor" className="w-16 h-16 object-cover rounded-md shadow-sm border border-slate-200" />
        ) : (
          <div className="w-16 h-16 rounded-md bg-slate-100 flex items-center justify-center text-slate-400 font-medium text-xl border border-slate-200">
            {visitor.firstName?.charAt(0) || 'V'}
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-slate-800 m-0">{visitor.firstName} {visitor.lastName}</h2>
            <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${getVisitStatusBadgeClass(visit)}`}>
              {getVisitStatusLabel(visit)}
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-1 mt-0.5">{visitor.companyName || 'Guest'}</p>
          {visit.passCode && (
             <div className="inline-block bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[11px] font-mono font-medium border border-slate-200">
               Pass: {visit.passCode}
             </div>
          )}
        </div>
      </div>

      <Descriptions 
        title={<span className="text-sm font-semibold text-slate-700">Visitor Details</span>} 
        bordered 
        column={2} 
        size="small" 
        className="mb-4"
        labelStyle={{ padding: '6px 10px', fontSize: '12px', width: '110px', backgroundColor: '#f8fafc' }}
        contentStyle={{ padding: '6px 10px', fontSize: '12px' }}
      >
        {loadingFields ? (
           <Descriptions.Item label="Loading..." span={2}>
             <div className="flex justify-center p-2">
               <div className="animate-spin w-4 h-4 border-2 border-slate-300 border-t-blue-500 rounded-full" />
             </div>
           </Descriptions.Item>
        ) : (
           formFields.map(field => {
             const val = getFieldValue(field.key);
             let displayVal = Array.isArray(val) ? val.join(', ') : val;
             if (displayVal && (field.type === 'date' || field.type === 'datetime' || field.key === 'expectedDate')) {
                displayVal = getLocalDateString(displayVal);
             }
             return (
               <Descriptions.Item key={field.key} label={getTranslatedLabel(field.label, user?.companyName)}>
                 {displayVal || 'N/A'}
               </Descriptions.Item>
             );
           })
        )}
      </Descriptions>

      <Descriptions 
        title={<span className="text-sm font-semibold text-slate-700">System Information</span>} 
        bordered 
        column={2} 
        size="small"
        labelStyle={{ padding: '6px 10px', fontSize: '12px', width: '110px', backgroundColor: '#f8fafc' }}
        contentStyle={{ padding: '6px 10px', fontSize: '12px' }}
      >
        <Descriptions.Item label="Check In">{formatLogTime(visit.checkInTime)}</Descriptions.Item>
        <Descriptions.Item label="Check Out">{formatLogTime(visit.checkOutTime)}</Descriptions.Item>
        <Descriptions.Item label="Created At">{new Date(visit.createdAt).toLocaleString()}</Descriptions.Item>
        <Descriptions.Item label="Duration">{getDuration()}</Descriptions.Item>
      </Descriptions>
    </Modal>
  );
};

export default VisitDetailsModal;
